import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { GenericEntityPreview } from "./GenericEntityPreview";
import { PromptEntityPreview } from "./PromptEntityPreview";
import { WorkflowEntityPreview } from "./WorkflowEntityPreview";

const ENTITY_TYPES_WITH_CUSTOM_RENDERERS = {
  PROMPT: "PROMPT",
  SNIPPET: "SNIPPET",
  WORKFLOW: "WORKFLOW",
} as const;

const isPositiveInteger = (value?: string) => {
  if (!value) {
    return false;
  }

  return /^\d+$/.test(value) && Number(value) > 0;
};

const EntityPreviewRendererPage = () => {
  const { workspaceId, entityType, entityId, versionNumber } = useParams();
  const [isReady, setIsReady] = useState(false);

  const normalizedEntityType = (entityType || "").toUpperCase();

  const hasValidBaseParams = useMemo(() => {
    if (!workspaceId || !entityType || !entityId) {
      return false;
    }

    if (!isPositiveInteger(workspaceId)) {
      return false;
    }

    if (versionNumber && !isPositiveInteger(versionNumber)) {
      return false;
    }

    return true;
  }, [workspaceId, entityId, entityType, versionNumber]);

  const handleReadyChange = useCallback((nextIsReady: boolean) => {
    setIsReady(nextIsReady);
  }, []);

  const previewContent = useMemo(() => {
    if (!workspaceId || !entityId || !hasValidBaseParams) {
      return null;
    }

    if (
      normalizedEntityType === ENTITY_TYPES_WITH_CUSTOM_RENDERERS.PROMPT ||
      normalizedEntityType === ENTITY_TYPES_WITH_CUSTOM_RENDERERS.SNIPPET
    ) {
      return (
        <PromptEntityPreview
          workspaceId={workspaceId}
          entityId={entityId}
          versionNumber={versionNumber}
          onReadyChange={handleReadyChange}
        />
      );
    }

    if (normalizedEntityType === ENTITY_TYPES_WITH_CUSTOM_RENDERERS.WORKFLOW) {
      return (
        <WorkflowEntityPreview
          workspaceId={workspaceId}
          entityId={entityId}
          versionNumber={versionNumber}
          onReadyChange={handleReadyChange}
        />
      );
    }

    return (
      <GenericEntityPreview
        entityType={normalizedEntityType || "UNKNOWN"}
        entityId={entityId}
        versionNumber={versionNumber}
        onReadyChange={handleReadyChange}
      />
    );
  }, [
    entityId,
    handleReadyChange,
    hasValidBaseParams,
    normalizedEntityType,
    versionNumber,
    workspaceId,
  ]);

  if (!hasValidBaseParams || !previewContent) {
    return (
      <div className="fixed inset-0 z-50 bg-slate-100">
        <div
          className="h-full w-full border border-slate-200 bg-white p-10 shadow-sm"
          data-preview-ready="true"
          data-preview-surface
        >
          <span className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-medium tracking-wide text-rose-700">
            INVALID PARAMS
          </span>
          <h1 className="mt-6 text-3xl font-semibold tracking-tight text-slate-900">
            Entity preview could not be rendered.
          </h1>
          <p className="mt-4 text-sm text-slate-600">
            Check workspace, entity type, entity id, and version route parameters.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-100">
      <div
        className="h-full w-full"
        data-preview-ready={isReady ? "true" : undefined}
        data-preview-surface
      >
        {previewContent}
      </div>
    </div>
  );
};

export default EntityPreviewRendererPage;
